import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, List as ListIcon, Plus, Bot, Settings2, Users, AlertTriangle, User } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import WeekGrid from "../components/agenda/WeekGrid";
import EventModal from "../components/agenda/EventModal";
import ProposalsPanel from "../components/agenda/ProposalsPanel";
import AgendaSettingsPanel from "../components/agenda/AgendaSettingsPanel";
import { startOfWeek, addDays, fmtDayLong, fmtTime, memberColor, TYPE_PILL } from "../components/agenda/agendaUtils";
import { ListRow, Section } from "../components/mobile/ListRow";
import { useT, locale } from "../lib/i18n";

const isMobileViewport = () => window.matchMedia("(max-width: 1023px)").matches;

export default function AgendaPage() {
  const t = useT();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "agenda";
  const setTab = (t) => { const n = new URLSearchParams(params); n.set("tab", t); setParams(n, { replace: true }); };

  const [scope, setScope] = useState("mine");
  const [view, setView] = useState(() => (isMobileViewport() ? "list" : "week"));
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [events, setEvents] = useState([]);
  const [team, setTeam] = useState({ members: [], is_admin: false });
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [proposals, setProposals] = useState([]);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [modal, setModal] = useState(null); // {event} | {initialStart}
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState("");

  const loadEvents = useCallback(async () => {
    const from = addDays(weekStart, -35).toISOString();
    const to = addDays(weekStart, 63).toISOString();
    const q = new URLSearchParams({ scope, from, to });
    if (scope === "team" && selectedMembers.size) q.set("user_ids", [...selectedMembers].join(","));
    const { data } = await api.get(`/agenda/events?${q.toString()}`);
    setEvents(data.events || []);
  }, [scope, weekStart, selectedMembers]);

  const loadProposals = useCallback(async () => {
    const [p, all] = await Promise.all([api.get("/agenda/proposals?status=pending"), api.get("/agenda/proposals?status=all")]);
    setProposals(p.data || []);
    setHistory((all.data || []).filter((x) => x.status !== "pending"));
  }, []);

  useEffect(() => {
    api.get("/agenda/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/agenda/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/contacts").then((r) => setContacts(Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => {});
    loadProposals();
  }, [loadProposals]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const weekEvents = useMemo(() => {
    const end = addDays(weekStart, 7);
    return events.filter((e) => { const t = new Date(e.start); return t >= weekStart && t < end; });
  }, [events, weekStart]);
  const upcoming = useMemo(() => events.filter((e) => new Date(e.start) >= new Date()).slice(0, 60), [events]);
  const conflicts = useMemo(() => events.filter((e) => (e.conflicts_with || []).length), [events]);

  const scan = async () => {
    setScanning(true); setScanMsg("");
    try {
      const { data } = await api.post("/agenda/scan");
      setScanMsg(`${t("{n} berichten gescand", { n: data.scanned })} · ${data.proposals === 1 ? t("{n} nieuw voorstel", { n: 1 }) : t("{n} nieuwe voorstellen", { n: data.proposals })}${data.auto_added ? ` · ${t("{n} automatisch toegevoegd", { n: data.auto_added })}` : ""}`);
      await Promise.all([loadProposals(), loadEvents()]);
    } finally { setScanning(false); }
  };

  const toggleMember = (id) => setSelectedMembers((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const tabs = [
    { k: "agenda", l: "Agenda", icon: CalendarDays },
    { k: "proposals", l: `${t("Voorstellen")}${proposals.length ? ` (${proposals.length})` : ""}`, icon: Bot },
    { k: "settings", l: t("Instellingen"), icon: Settings2 },
  ];

  return (
    <Layout
      title="Agenda"
      subtitle={t("Centrale agenda · AI-afspraakherkenning · gedeeld met je agency")}
      right={
        <button onClick={() => setModal({ initialStart: new Date().toISOString() })} className="pm-btn-primary h-10 w-10 lg:w-auto lg:h-9 px-0 lg:px-3 rounded-full lg:rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95" data-testid="agenda-new-event" aria-label={t("Nieuwe afspraak")}>
          <Plus size={14} /> <span className="hidden lg:inline">{t("Nieuwe afspraak")}</span>
        </button>
      }
    >
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto whitespace-nowrap" data-testid="agenda-tabs">
        {tabs.map((tb) => (
          <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`agenda-tab-${tb.k}`}
            className={`px-4 h-10 text-sm font-bold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === tb.k ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <tb.icon size={13} /> {tb.l}
          </button>
        ))}
      </div>

      {tab === "agenda" && (
        <>
          <div className="pm-card p-3 flex flex-wrap items-center gap-2 mb-4" data-testid="agenda-toolbar">
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
              <button onClick={() => setScope("mine")} className={`h-8 px-3 text-xs font-bold flex items-center gap-1 ${scope === "mine" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="agenda-scope-mine"><User size={12} /> {t("Mijn agenda")}</button>
              <button onClick={() => setScope("team")} className={`h-8 px-3 text-xs font-bold flex items-center gap-1 ${scope === "team" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="agenda-scope-team"><Users size={12} /> Team</button>
            </div>
            {scope === "team" && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="agenda-member-filter">
                {team.members.map((m) => {
                  const on = selectedMembers.size === 0 || selectedMembers.has(m.user_id);
                  const hidden = !m.share_with_team && !team.is_admin && m.user_id !== user?.id;
                  return (
                    <button key={m.user_id} onClick={() => toggleMember(m.user_id)} disabled={hidden} title={hidden ? t("Deelt agenda niet") : m.team_permission === "editor" ? t("Editor-rechten") : t("Alleen inzien")}
                      className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-full border text-[11px] font-bold ${on && !hidden ? "border-slate-900 bg-white text-slate-900" : "border-slate-200 bg-slate-50 text-slate-400"} ${hidden ? "line-through" : ""}`}
                      data-testid={`agenda-member-${m.user_id}`}>
                      <span className="w-2 h-2 rounded-full" style={{ background: memberColor(team.members, m.user_id) }} />
                      {m.name?.split(" ")[0]}{m.team_permission === "editor" && <span className="text-[9px] text-emerald-600">✎</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="ml-auto inline-flex items-center gap-2">
              {conflicts.length > 0 && (
                <span className="pm-pill pm-pill-red text-[10px] flex items-center gap-1" data-testid="agenda-conflict-count"><AlertTriangle size={10} /> {conflicts.length > 1 ? t("{n} conflicten", { n: conflicts.length }) : t("{n} conflict", { n: conflicts.length })}</span>
              )}
              <button onClick={() => setView("week")} className={`hidden lg:flex pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold items-center gap-1 ${view === "week" ? "bg-slate-900 text-white border-slate-900" : ""}`} data-testid="agenda-view-week"><CalendarDays size={12} /> {t("Week")}</button>
              <button onClick={() => setView("list")} className={`hidden lg:flex pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold items-center gap-1 ${view === "list" ? "bg-slate-900 text-white border-slate-900" : ""}`} data-testid="agenda-view-list"><ListIcon size={12} /> {t("Lijst")}</button>
            </div>
          </div>

          {view === "week" && (
            <div className="pm-card p-4" data-testid="agenda-week-view">
              <div className="flex items-center gap-3 mb-3">
                <button onClick={() => setWeekStart((d) => addDays(d, -7))} className="pm-btn-secondary h-9 w-9 rounded-lg flex items-center justify-center" data-testid="agenda-prev-week"><ChevronLeft size={14} /></button>
                <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="agenda-today">{t("Vandaag")}</button>
                <button onClick={() => setWeekStart((d) => addDays(d, 7))} className="pm-btn-secondary h-9 w-9 rounded-lg flex items-center justify-center" data-testid="agenda-next-week"><ChevronRight size={14} /></button>
                <div className="font-display text-lg font-bold tracking-tight">
                  {weekStart.toLocaleDateString(locale(), { day: "2-digit", month: "short" })} — {addDays(weekStart, 6).toLocaleDateString(locale(), { day: "2-digit", month: "short", year: "numeric" })}
                </div>
                <span className="ml-auto text-[11px] text-slate-500 font-mono-tab" data-testid="agenda-week-count">{weekEvents.length} {t("afspraken")}</span>
              </div>
              <WeekGrid weekStart={weekStart} events={weekEvents} members={team.members} onSelect={(e) => setModal({ event: e })} onCreateAt={(d) => { const s = new Date(d); s.setHours(10, 0, 0, 0); setModal({ initialStart: s.toISOString() }); }} />
            </div>
          )}

          {view === "list" && (
            <div className="lg:hidden -mx-4" data-testid="agenda-mobile-list">
              {upcoming.length === 0 && <div className="mx-4 my-3 py-5 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">{t("Geen komende afspraken.")}</div>}
              {Object.entries(upcoming.reduce((acc, e) => { const k = new Date(e.start).toDateString(); (acc[k] = acc[k] || []).push(e); return acc; }, {})).map(([day, evs]) => (
                <Section key={day} title={new Date(evs[0].start).toLocaleDateString(locale(), { weekday: "long", day: "numeric", month: "long" })} testid={`agenda-mobile-day-${day.replace(/\s/g, "-")}`}>
                  {evs.map((e) => (
                    <ListRow key={e.id} tone={(e.conflicts_with || []).length ? "red" : "none"} leading={fmtTime(e.start)} title={e.title}
                      meta={[scope === "team" ? e.owner_name : null, e.client_name, e.contact_name, e.location].filter(Boolean).join(" · ") || null}
                      onClick={() => setModal({ event: e })} testid={`agenda-list-event-${e.id}`} />
                  ))}
                </Section>
              ))}
            </div>
          )}
          {view === "list" && (
            <div className="hidden lg:block pm-card p-5" data-testid="agenda-list-view">
              <h3 className="font-display text-xl font-bold tracking-tight mb-4">{t("Komende afspraken")} · {upcoming.length}</h3>
              <div className="space-y-2">
                {upcoming.map((e) => (
                  <button key={e.id} onClick={() => setModal({ event: e })} data-testid={`agenda-desktop-event-${e.id}`}
                    className={`w-full text-left flex items-center gap-4 px-4 py-3 rounded-xl border transition-colors ${(e.conflicts_with || []).length ? "border-red-300 bg-red-50/50" : "border-slate-200 hover:bg-slate-50/60"}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: memberColor(team.members, e.owner_user_id) }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">{e.title}{e.source === "ai" && <Bot size={11} className="text-emerald-600" />}</div>
                      <div className="text-xs text-slate-500 truncate">{e.owner_name}{e.client_name ? ` · ${e.client_name}` : ""}{e.contact_name ? ` · ${e.contact_name}` : ""}{e.location ? ` · ${e.location}` : ""}</div>
                    </div>
                    <span className={`pm-pill ${TYPE_PILL[e.event_type] || "pm-pill-slate"}`}>{(e.event_type || "event").replace("_", " ")}</span>
                    <div className="font-mono-tab text-sm text-slate-700 w-40 text-right">{fmtDayLong(e.start)}</div>
                  </button>
                ))}
                {upcoming.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="agenda-list-empty">{t("Geen komende afspraken.")}</div>}
              </div>
            </div>
          )}
        </>
      )}

      {tab === "proposals" && (
        <>
          {scanMsg && <div className="text-xs text-emerald-700 mb-3" data-testid="agenda-scan-result">{scanMsg}</div>}
          <ProposalsPanel proposals={proposals} clients={clients} history={history} scanning={scanning} onScan={scan}
            onDecided={async () => { await Promise.all([loadProposals(), loadEvents()]); }} />
        </>
      )}

      {tab === "settings" && settings && <AgendaSettingsPanel settings={settings} onSaved={(s) => { setSettings(s); api.get("/agenda/team").then((r) => setTeam(r.data)); }} />}

      {modal && (
        <EventModal event={modal.event} initialStart={modal.initialStart} me={user} members={team.members} isAdmin={team.is_admin}
          clients={clients} contacts={contacts} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await loadEvents(); }} />
      )}
    </Layout>
  );
}
